/** GA4 reports an empty string as a real value, so drop the key rather than send it. */
export const withoutEmptyValues = (obj) => Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== ""));
const flattenMeals = (orderInfo) => { var _a, _b; return (_b = (_a = orderInfo.days) === null || _a === void 0 ? void 0 : _a.flatMap((day) => day.meal_dates.flatMap((mealDate) => mealDate.meal_list))) !== null && _b !== void 0 ? _b : []; };
export const firstCouponName = (orderInfo) => { var _a, _b; return (_b = (_a = orderInfo.Coupons) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.couponName; };
/**
 * GA4 value excludes tax and shipping, which purchase reports as their own params.
 * orderInfo.total is the charged amount and includes both, so sending it as value
 * double-counts them in revenue. Rounded to cents to keep float noise out of reports.
 */
export const orderValue = (orderInfo) => { var _a, _b; return Math.round((orderInfo.total - ((_a = orderInfo.tax) !== null && _a !== void 0 ? _a : 0) - ((_b = orderInfo.shipping) !== null && _b !== void 0 ? _b : 0)) * 100) / 100; };
/**
 * Ingredient swaps carry an upcharge that meal_cost excludes but the order total
 * includes, so GA4 would report value above the sum of its items without this.
 * Per unit, matching how the app prices (customization + base) x quantity.
 */
const customizationUpcharge = (meal) => { var _a, _b; return (_b = (_a = meal.recipeChanges) === null || _a === void 0 ? void 0 : _a.reduce((sum, change) => sum + change.replacement_price, 0)) !== null && _b !== void 0 ? _b : 0; };
/**
 * Per-meal GA4 items for an order. Shared by checkout and the thank-you page so both
 * report the same item shape for the same order.
 */
export const buildItemsFromOrderInfo = (orderInfo, itemBrand) => {
    var _a;
    const coupon = firstCouponName(orderInfo);
    const listId = orderInfo.program_id ? String(orderInfo.program_id) : orderInfo.program_sku;
    // Products are priced into orderInfo.total, so leaving them out would make
    // value exceed the sum of items. item_id matches what add_to_cart sends.
    const productItems = ((_a = orderInfo.product_items) !== null && _a !== void 0 ? _a : []).map((product) => {
        var _a;
        return withoutEmptyValues({
            item_id: String(product.productId),
            item_name: product.name,
            price: (_a = product.SalePrice) !== null && _a !== void 0 ? _a : product.cost,
            quantity: product.quantity,
            item_brand: itemBrand,
            item_category: "product",
            coupon,
        });
    });
    const mealItems = flattenMeals(orderInfo).map((meal) => {
        var _a;
        return withoutEmptyValues({
            item_id: String(meal.ProgramMealId),
            item_name: meal.recipeName,
            price: meal.meal_cost + customizationUpcharge(meal),
            quantity: meal.quantity,
            item_brand: itemBrand,
            item_category: meal.meal_type,
            item_category2: orderInfo.recipe_type_name || orderInfo.PlanName,
            item_variant: ((_a = meal.recipeChanges) === null || _a === void 0 ? void 0 : _a.length) ? "customized" : "standard",
            item_list_id: listId,
            item_list_name: orderInfo.FlowName,
            coupon,
        });
    });
    return [...mealItems, ...productItems];
};
/** Takes the amount rather than a gift card model so checkout (before import) and thank-you report the same item. */
export const buildGiftCardItem = (amount, itemBrand) => withoutEmptyValues({
    item_id: "gift_card",
    item_name: "Gift Card",
    item_category: "gift_card",
    item_brand: itemBrand,
    price: amount,
    quantity: 1,
});
export const buildCartItem = (input, itemBrand) => withoutEmptyValues(Object.assign(Object.assign({}, input), { item_brand: itemBrand }));
export const sumItemsValue = (items) => items.reduce((total, item) => total + item.price * item.quantity, 0);
